import React, { useEffect, useState, ChangeEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IGetOrderDTO from "../../../models/DTO/IGetOrderDTO";
import IOnGetOrderDTO from "../../../models/DTO/IOnGetOrderDTO";
import IUpdateOrderEntryDetailDTO from "../../../models/DTO/IUpdateOrderEntryDetailDTO";
import IOnUpdateOrderEntryDetailDTO from "../../../models/DTO/IOnUpdateOrderEntryDetailDTO";
import IOrder from "../../../models/IOrder";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTitle from "../../shared/OwTitle/OwTitle";
import UserService from "../../../services/UserService";
import styled from "styled-components";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import IPayment from "../../../models/IPayment";
import { CreditCardBrandIcon } from "../../shared/CreditCardBrandIcon/CreditCardBrandIcon";
import OwButton from "../../shared/OwButton/OwButton";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import { QtyDetail } from "../../shared/QtyDetail/QtyDetail";
import { FilesDownloadSection } from "../../shared/FilesDownloadSection/FilesDownloadSection";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { KitItemsDisplay } from "../../shared/KitItemsDisplay/KitItemsDisplay";
import HelperService from "../../../services/HelperService";
import { useAppContextNonNull } from "@contexts/AppContext";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwTextArea from "../../shared/OwTextArea/OwTextArea";
import OwEditModeToggle from "../../shared/OwEditModeToggle/OwEditModeToggle";
import { OrdersService } from "@components/orders/Orders/OrdersService";
import SupplierService from "@services/SupplierService";
import OrderEntryService from "@services/OrderEntryService";

type Props = {};

export const EcommerceOrderDetails: React.FC<Props> = (props) => {
    const { userModel, supplierModel, vendorModel, socket, uiContext } = useAppContextNonNull();

    const params = useParams();
    const history = useNavigate();

    const { guid } = params;

    const [order, setOrder] = useState<IOrder>();
    const [paymentDetail, setPaymentDetail] = useState<IPayment>();
    const [paymentMethodDetail, setPaymentMethodDetail] = useState<{ brand: string; last4: string }>();

    const [allowEditOrderDetails, setAllowEditOrderDetails] = useState(false);

    // Edit mode states
    const [poNoEditMode, setPoNoEditMode] = useState(false);
    const [noteEditMode, setNoteEditMode] = useState(false);
    const [editedPoNo, setEditedPoNo] = useState("");
    const [editedNote, setEditedNote] = useState("");

    const onGetOrder = (data: IOnGetOrderDTO) => {
        setOrder(data.order);
        setEditedPoNo(data.order.po_no || "");
        setEditedNote(data.order.note || "");

        const orderStateDetail = OrderEntryService.getCurrentOrderStep(data.order, supplierModel);

        if (orderStateDetail) {
            setAllowEditOrderDetails(orderStateDetail.isVendorReadOnly || supplierModel.ecomm_edit_order_details || false);
        }

        if (data.order.Payments?.length) {
            setPaymentDetail(data.order.Payments[0]);

            const captureDetail = JSON.parse(data.order.Payments[0].capture_detail!);

            if (captureDetail) {
                const last4 = captureDetail.charges.data[0].payment_method_details.card.last4;
                const brand = captureDetail.charges.data[0].payment_method_details.card.brand;

                setPaymentMethodDetail({ last4, brand });
            }
        }
    };

    const onUpdateOrderEntryDetail = (data: IOnUpdateOrderEntryDetailDTO) => {
        setEditedPoNo(data.order_detail.po_no || "");
        setEditedNote(data.order_detail.note || "");
        setPoNoEditMode(false);
        setNoteEditMode(false);

        getOrder();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.OrderOnGetOrder, fn: onGetOrder },
        { endpoint: EndpointsEnum.OrderEntryOnUpdateOrderEntryDetail, fn: onUpdateOrderEntryDetail },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getOrder();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const getOrder = () => {
        if (guid) {
            const payload: IGetOrderDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                orderGuid: guid,
            };

            socket.emit(EndpointsEnum.OrderGetOrder, payload);
        }
    };

    const viewInvoice = () => {
        history(`/ecomm/orders/invoice/${guid}`);
    };

    // PO Number edit handlers
    const togglePoNoEditMode = () => {
        setPoNoEditMode(true);
        setEditedPoNo(order?.po_no || "");
    };

    const cancelPoNoEdit = () => {
        setPoNoEditMode(false);
        setEditedPoNo(order?.po_no || "");
    };

    const savePoNo = () => {
        if (order) {
            const payload: IUpdateOrderEntryDetailDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                orderGuid: order.guid,
                po_no: editedPoNo,
            };
            socket.emit(EndpointsEnum.OrderEntryUpdateOrderEntryDetail, payload);
        }
    };

    const handlePoNoChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEditedPoNo(e.target.value);
    };

    // Note edit handlers
    const toggleNoteEditMode = () => {
        setNoteEditMode(true);
        setEditedNote(order?.note || "");
    };

    const cancelNoteEdit = () => {
        setNoteEditMode(false);
        setEditedNote(order?.note || "");
    };

    const saveNote = () => {
        if (order) {
            const payload: IUpdateOrderEntryDetailDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                orderGuid: order.guid,
                note: editedNote,
            };
            socket.emit(EndpointsEnum.OrderEntryUpdateOrderEntryDetail, payload);
        }
    };

    const handleNoteChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setEditedNote(e.target.value);
    };

    return (
        <div className={"EcommerceOrderDetails container"}>
            <div className={"mt-2"}>
                <Link to={"/ecomm/orders"}>
                    <OwLabel className={"btn btn-sm btn-primary pointer"} title={"back"} leftIconClass={"fas fa-arrow-left"} />
                </Link>
            </div>
            {!order ? (
                <div>
                    <ActivityIndicator active={true} />
                    <OwLabel title={"loading"} />
                </div>
            ) : (
                <div>
                    <div>
                        <OwTitle title={"order_details"} />
                    </div>

                    <div className={"d-flex justify-content-between"}>
                        <div className={"d-flex"}>
                            <div>
                                <OwLabel title={"ordered_on"} /> {UserService.parseDate(order.created_at)}
                            </div>
                            <div className={"ms-3 me-3 text-muted"}>|</div>
                            <div>
                                <OwLabel title={"order_no"} /> {order.name}
                            </div>
                            <div className={"ms-3 me-3 text-muted"}>|</div>
                            <div className={"d-flex align-items-center"}>
                                {allowEditOrderDetails && (
                                    <OwEditModeToggle
                                        className={"me-2"}
                                        editMode={poNoEditMode}
                                        cancelEditFn={cancelPoNoEdit}
                                        saveEditFn={savePoNo}
                                        toggleEditFn={togglePoNoEditMode}
                                    />
                                )}
                                <OwLabel title={"po_no"} className={"me-2"} />
                                {poNoEditMode ? (
                                    <OwFnInput
                                        name="po_no"
                                        value={editedPoNo}
                                        fn={handlePoNoChange}
                                        placeholder="po_no"
                                        className="w-10r"
                                    />
                                ) : (
                                    <span>{order.po_no || ""}</span>
                                )}
                            </div>
                        </div>

                        <div>
                            <OwButton title={"view_or_print_invoice"} fn={viewInvoice} />
                        </div>
                    </div>

                    <SectionContainerDiv className={"mt-4 d-flex justify-content-between"}>
                        <div>
                            <div>
                                <OwTitle title={"shipping_address"} tag={"h6"} isBold={true} />
                                <AddressDisplay address={order.Ship_To} showName={true} showCompanyName={true} />
                            </div>
                            <div className={"mt-4"}>
                                <div className={"d-flex align-items-center mb-2"}>
                                    {allowEditOrderDetails && (
                                        <OwEditModeToggle
                                            className={"me-2"}
                                            editMode={noteEditMode}
                                            cancelEditFn={cancelNoteEdit}
                                            saveEditFn={saveNote}
                                            toggleEditFn={toggleNoteEditMode}
                                        />
                                    )}
                                    <OwTitle title={"note"} tag={"h6"} isBold={true} />
                                </div>
                                {noteEditMode ? (
                                    <OwTextArea
                                        name="note"
                                        value={editedNote}
                                        fn={handleNoteChange}
                                        placeholder="note"
                                        rows={3}
                                        className="w-100"
                                    />
                                ) : order.note ? (
                                    <div>{order.note}</div>
                                ) : null}
                            </div>
                        </div>

                        <div>
                            {!paymentMethodDetail ? null : (
                                <>
                                    <OwTitle title={"payment_method"} tag={"h6"} isBold={true} />
                                    <div className={"d-flex align-items-center"}>
                                        <CardIcon>
                                            <CreditCardBrandIcon brand={paymentMethodDetail.brand} />
                                        </CardIcon>
                                        <div className={"ms-2"}>**** {paymentMethodDetail.last4}</div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className={"w-15r"}>
                            <OwTitle title={"order_summary"} tag={"h6"} isBold={true} />
                            <div className={"d-flex justify-content-between"}>
                                <OwLabel title={"subtotal"} />
                                {HelperService.formatCurrency({
                                    value: order.sub_total || 0,
                                    currency: "USD",
                                    precision: supplierModel.pricing_precision || 2,
                                })}
                            </div>

                            {order.discount_total && order.discount_total > 0 ? (
                                <div className={"d-flex justify-content-between"}>
                                    <OwLabel title={"discount"} /> -
                                    {HelperService.formatCurrency({
                                        value: order.discount_total || 0,
                                        currency: "USD",
                                        precision: supplierModel.pricing_precision || 2,
                                    })}
                                </div>
                            ) : null}

                            <div className={"d-flex justify-content-between"}>
                                <OwLabel title={"tax"} />
                                {HelperService.formatCurrency({
                                    value: order.tax || 0,
                                    currency: "USD",
                                    precision: supplierModel.pricing_precision || 2,
                                })}
                            </div>

                            <div className={"d-flex justify-content-between"}>
                                <OwLabel title={"shipping"} />
                                {HelperService.formatCurrency({
                                    value: order.shipping || 0,
                                    currency: "USD",
                                    precision: supplierModel.pricing_precision || 2,
                                })}
                            </div>

                            <div className={"d-flex justify-content-between"}>
                                <b>
                                    <OwLabel title={"grand_total"} />
                                </b>

                                <b>
                                    {HelperService.formatCurrency({
                                        value: order.grand_total || 0,
                                        currency: "USD",
                                        precision: supplierModel.pricing_precision || 2,
                                    })}
                                </b>
                            </div>
                        </div>
                    </SectionContainerDiv>

                    {order.Order_Items.map((orderItem) => {
                        return (
                            <SectionContainerDiv className={"mt-2 d-flex"} key={orderItem.guid}>
                                <div>
                                    <OwS3ImageViewer
                                        catalogItem={orderItem.Catalog_Item}
                                        isMicroView={false}
                                        attachmentIndex={0}
                                        imageIndex={IImageSizeEnum.THUMB}
                                        className={"w-5r h-5r"}
                                        includeAllImages={false}
                                        hideEmptyImage={false}
                                    />
                                </div>
                                <div className={"ms-2"}>
                                    <div>{orderItem.Catalog_Item?.name}</div>
                                    <div className={"d-flex"}>
                                        <QtyDetail item={orderItem} className={"text-end"} />
                                        <OwLabel title={"at"} className={"ms-2 me-2"} />
                                        {HelperService.formatCurrency({
                                            value: orderItem.cost_price_frozen || 0,
                                            currency: "USD",
                                            precision: supplierModel.pricing_precision || 2,
                                        })}
                                    </div>
                                    {orderItem.Catalog_Item ? (
                                        <FilesDownloadSection catalogItem={orderItem.Catalog_Item} readOnly={true} />
                                    ) : null}
                                    <KitItemsDisplay orderItem={orderItem} />
                                </div>
                            </SectionContainerDiv>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const SectionContainerDiv = styled.div`
    border: 1px solid var(--main-text-color);
    padding: 0.5rem;
    border-radius: 5px;
`;

const CardIcon = styled.div`
    font-size: 1.3rem;
`;
